import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface AddieOutline {
  analysis?: {
    learner_needs?: string;
    learning_context?: string;
    performance_gaps?: string;
  };
  design?: {
    learning_objectives?: string[];
    assessment_strategy?: string;
    instructional_approach?: string;
  };
  development?: {
    content_structure?: string;
    activities?: string[];
    materials_needed?: string[];
  };
  implementation?: {
    delivery_method?: string;
    timeline?: string;
    logistics?: string;
  };
  evaluation?: {
    formative_evaluation?: string;
    summative_evaluation?: string;
    success_metrics?: string[];
  };
}

interface AddieOutlineViewProps {
  outline: AddieOutline;
}

const AddieOutlineView = ({ outline }: AddieOutlineViewProps) => {
  return (
    <div className="space-y-6">
      {/* Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default">Phase 1</Badge>
            <CardTitle>Analysis</CardTitle>
          </div>
          <CardDescription>Understanding learner needs and context</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {outline.analysis?.learner_needs && (
            <div>
              <h4 className="font-semibold mb-2">Learner Needs</h4>
              <p className="text-muted-foreground">{outline.analysis.learner_needs}</p>
            </div>
          )}
          {outline.analysis?.learning_context && (
            <div>
              <h4 className="font-semibold mb-2">Learning Context</h4>
              <p className="text-muted-foreground">{outline.analysis.learning_context}</p>
            </div>
          )}
          {outline.analysis?.performance_gaps && (
            <div>
              <h4 className="font-semibold mb-2">Performance Gaps</h4>
              <p className="text-muted-foreground">{outline.analysis.performance_gaps}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Design */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default">Phase 2</Badge>
            <CardTitle>Design</CardTitle>
          </div>
          <CardDescription>Planning the learning experience</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {outline.design?.learning_objectives && outline.design.learning_objectives.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Learning Objectives</h4>
              <ul className="list-disc list-inside space-y-1">
                {outline.design.learning_objectives.map((obj, i) => (
                  <li key={i} className="text-muted-foreground">{obj}</li>
                ))}
              </ul>
            </div>
          )}
          {outline.design?.assessment_strategy && (
            <div>
              <h4 className="font-semibold mb-2">Assessment Strategy</h4>
              <p className="text-muted-foreground">{outline.design.assessment_strategy}</p>
            </div>
          )}
          {outline.design?.instructional_approach && (
            <div>
              <h4 className="font-semibold mb-2">Instructional Approach</h4>
              <p className="text-muted-foreground">{outline.design.instructional_approach}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Development */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default">Phase 3</Badge>
            <CardTitle>Development</CardTitle>
          </div>
          <CardDescription>Creating the learning materials</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {outline.development?.content_structure && (
            <div>
              <h4 className="font-semibold mb-2">Content Structure</h4>
              <p className="text-muted-foreground">{outline.development.content_structure}</p>
            </div>
          )}
          {outline.development?.activities && outline.development.activities.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Activities</h4>
              <ul className="list-disc list-inside space-y-1">
                {outline.development.activities.map((activity, i) => (
                  <li key={i} className="text-muted-foreground">{activity}</li>
                ))}
              </ul>
            </div>
          )}
          {outline.development?.materials_needed && outline.development.materials_needed.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Materials Needed</h4>
              <ul className="list-disc list-inside space-y-1">
                {outline.development.materials_needed.map((material, i) => (
                  <li key={i} className="text-muted-foreground">{material}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Implementation */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default">Phase 4</Badge>
            <CardTitle>Implementation</CardTitle>
          </div>
          <CardDescription>Delivering the learning experience</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {outline.implementation?.delivery_method && (
            <div>
              <h4 className="font-semibold mb-2">Delivery Method</h4>
              <p className="text-muted-foreground">{outline.implementation.delivery_method}</p>
            </div>
          )}
          {outline.implementation?.timeline && (
            <div>
              <h4 className="font-semibold mb-2">Timeline</h4>
              <p className="text-muted-foreground">{outline.implementation.timeline}</p>
            </div>
          )}
          {outline.implementation?.logistics && (
            <div>
              <h4 className="font-semibold mb-2">Logistics</h4>
              <p className="text-muted-foreground">{outline.implementation.logistics}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Evaluation */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Badge variant="default">Phase 5</Badge>
            <CardTitle>Evaluation</CardTitle>
          </div>
          <CardDescription>Measuring effectiveness and success</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {outline.evaluation?.formative_evaluation && (
            <div>
              <h4 className="font-semibold mb-2">Formative Evaluation</h4>
              <p className="text-muted-foreground">{outline.evaluation.formative_evaluation}</p>
            </div>
          )}
          {outline.evaluation?.summative_evaluation && (
            <div>
              <h4 className="font-semibold mb-2">Summative Evaluation</h4>
              <p className="text-muted-foreground">{outline.evaluation.summative_evaluation}</p>
            </div>
          )}
          {outline.evaluation?.success_metrics && outline.evaluation.success_metrics.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Success Metrics</h4>
              <ul className="list-disc list-inside space-y-1">
                {outline.evaluation.success_metrics.map((metric, i) => (
                  <li key={i} className="text-muted-foreground">{metric}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AddieOutlineView;
